import React from "react";

const Pagination = ({
  currentPage = 1,
  totalPages = 5,
  handlePageChange = () => {},
  maxVisiblePages = 5,
  isRTL = false, // Add RTL support prop
}) => {
  const isPrevDisabled = currentPage <= 1;
  const isNextDisabled = currentPage >= totalPages;

  // Convert Western/Hindu numerals to Arabic-Indic numerals
  const toArabicNumerals = (num) => {
    if (!isRTL) return num.toString();

    const arabicNumerals = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];
    return num
      .toString()
      .replace(/\d/g, (digit) => arabicNumerals[parseInt(digit)]);
  };

  const handlePrevPage = () => {
    if (!isPrevDisabled) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (!isNextDisabled) {
      handlePageChange(currentPage + 1);
    }
  };

  const handlePageClick = (page) => {
    if (page !== currentPage) {
      handlePageChange(page);
    }
  };

  // Generate page numbers to display
  const getVisiblePages = () => {
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      const pages = [];
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
      return pages;
    }

    // Always show first and last pages with middle pages
    const pages = [];
    
    // Always add first page
    pages.push(1);
    
    // Calculate middle pages based on current position
    const middleStart = Math.max(2, currentPage - 1);
    const middleEnd = Math.min(totalPages - 1, currentPage + 1);
    
    // Add ellipsis before middle pages if there's a gap
    if (middleStart > 2) {
      pages.push('...');
    }
    
    // Add middle pages (avoiding duplicates with first/last)
    for (let i = middleStart; i <= middleEnd; i++) {
      if (i !== 1 && i !== totalPages) {
        pages.push(i);
      }
    }
    
    // Add ellipsis after middle pages if there's a gap
    if (middleEnd < totalPages - 1) {
      pages.push('...');
    }
    
    // Always add last page (if it's not the same as first)
    if (totalPages > 1) {
      pages.push(totalPages);
    }
    
    return pages;
  };

  const visiblePages = getVisiblePages();

  return (
    <div className="pagination" dir={isRTL ? "rtl" : "ltr"}>
      {/* Previous button with border container */}
      <div
        className={`pagination-arrow ${isPrevDisabled ? "disabled" : ""}`}
        onClick={handlePrevPage}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{
            transform: isRTL ? "scaleX(-1)" : "none", // Flip arrow in RTL
          }}
        >
          <path
            d="M9.99925 13.2797L5.65258 8.93306C5.13924 8.41973 5.13924 7.57973 5.65258 7.06639L9.99925 2.71973"
            stroke="url(#paint0_linear_2070_6101)"
            stroke-width="1.5"
            stroke-miterlimit="10"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <defs>
            <linearGradient
              id="paint0_linear_2070_6101"
              x1="7.63341"
              y1="2.71973"
              x2="7.63341"
              y2="13.2797"
              gradientUnits="userSpaceOnUse"
            >
              <stop stop-color="#292929" />
              <stop offset="1" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Page numbers */}
      {visiblePages.map((page, index) => {
        if (page === '...') {
          return (
            <span key={`ellipsis-${index}`} className="ellipsis">
              ...
            </span>
          );
        }
        
        return (
          <div
            key={page}
            className={page === currentPage ? "ActivePageNumber" : "page-number"}
            onClick={() => handlePageClick(page)}
          >
            {toArabicNumerals(page)}
          </div>
        );
      })}

      {/* Next button with border container */}
      <div
        className={`pagination-arrow ${isNextDisabled ? "disabled" : ""}`}
        onClick={handleNextPage}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{
            transform: isRTL ? "scaleX(-1)" : "none", // Flip arrow in RTL
          }}
        >
          <path
            d="M5.93945 13.2797L10.2861 8.93306C10.7995 8.41973 10.7995 7.57973 10.2861 7.06639L5.93945 2.71973"
            stroke="url(#paint0_linear_2070_6168)"
            stroke-width="1.5"
            stroke-miterlimit="10"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <defs>
            <linearGradient
              id="paint0_linear_2070_6168"
              x1="8.30529"
              y1="2.71973"
              x2="8.30529"
              y2="13.2797"
              gradientUnits="userSpaceOnUse"
            >
              <stop stop-color="#292929" />
              <stop offset="1" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      <style jsx>{`
        .pagination {
          display: flex;
          flex-direction: row;
          justify-content: center;
          align-items: center;
          gap: 16px;
          margin-top: 32px;
          margin-bottom: 32px;
          padding: 0 16px;
          height: auto;
          grid-column: 1 / -1;
        }

        .pagination-arrow {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 32px;
          height: 32px;
          border: 2px solid #741cad;
          border-radius: 8px;
          background: #ffffff;
          cursor: pointer;
          transition: all 0.2s ease;
          box-sizing: border-box;
        }

        .pagination-arrow:hover:not(.disabled) {
          background: #741cad;
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(116, 28, 173, 0.2);
        }

        .pagination-arrow:hover:not(.disabled) svg path {
          stroke: #ffffff;
        }

        .pagination-arrow.disabled {
          border-color: #e0e0e0;
          background: #f5f5f5;
          cursor: not-allowed;
          opacity: 0.6;
          padding: 0 !important;
        }

        .pagination-arrow:active:not(.disabled) {
          transform: translateY(0px);
          box-shadow: 0 2px 4px rgba(116, 28, 173, 0.2);
        }

        .pagination svg {
          transition: all 0.2s ease;
        }

        .ActivePageNumber {
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding: 12px;
          width: 34px;
          height: 34px;
          background: #5507cf;
          border: 1px solid #b2b2b2;
          border-radius: 8px;
          font-style: normal;
          font-weight: 700;
          font-size: 16px;
          line-height: 150%;
          text-align: center;
          color: #ffffff;
          cursor: pointer;
        }

        .page-number {
          box-sizing: border-box;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 12px;
          width: 34px;
          height: 34px;
          background: transparent;
          border: 1px solid #b2b2b2;
          border-radius: 8px;
          font-weight: 700;
          font-size: 16px;
          line-height: 150%;
          color: #666666;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .page-number:hover {
          background: #f0f0f0;
          color: #333333;
        }

        .ellipsis {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          color: #888888;
          font-size: 16px;
          user-select: none;
        }

        /* RTL Support */
        [dir="rtl"] .pagination {
          direction: rtl;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .pagination {
            gap: 12px;
            padding: 0 8px;
          }

          .pagination-arrow {
            min-width: 28px;
            height: 28px;
            padding: 4px 8px;
          }

          .pagination-arrow svg {
            width: 14px;
            height: 14px;
          }

          .ActivePageNumber,
          .page-number {
            min-width: 28px;
            height: 28px;
            font-size: 14px;
            padding: 4px 8px;
          }
        }

        @media (max-width: 480px) {
          .pagination {
            gap: 8px;
            margin-top: 24px;
            margin-bottom: 24px;
          }

          .pagination-arrow {
            min-width: 24px;
            height: 24px;
            padding: 2px 6px;
          }

          .pagination-arrow svg {
            width: 12px;
            height: 12px;
          }

          .ActivePageNumber,
          .page-number {
            min-width: 24px;
            height: 24px;
            font-size: 12px;
            padding: 2px 6px;
          }
        }
      `}</style>
    </div>
  );
};

export default Pagination;
